/**
 * ThemeProvider component
 *
 * A context provider that manages the application-wide theme settings.
 * Supports light and dark themes with consistent styling values.
 */
import React, { useState, useCallback, createContext, ReactNode, useEffect, useMemo } from "react";

import { logger } from "../utils/logger";
// Removed Tailwind config imports to prevent build error
// import resolveConfig from 'tailwindcss/resolveConfig';
// import tailwindConfig from '../../tailwind.config.js'; 

// Create a namespaced logger for theme
const themeLogger = logger.getNamespacedLogger('Theme');

// Define theme types
export type ThemeMode = 'light' | 'dark';

// Define the context interface
export interface ThemeContextType {
  /** Current theme mode ('light' or 'dark') */
  theme: ThemeMode;
  
  /** Function to toggle between light and dark themes */
  toggleTheme: () => void;
  
  /** Function to explicitly set the theme */
  setTheme: (theme: ThemeMode) => void;
  
  /** Returns true if the current theme is dark */
  isDarkTheme: boolean;
  
  /** CSS class names for themed elements (changes based on current theme) */
  themeClasses: {
    /** Base container background for theme */
    container: string;
    
    /** Text color class for the theme */
    text: string;
    
    /** Border colors for the theme */
    border: string;
    
    /** Card/element background for the theme */
    card: string;
    
    /** Accent colors for the theme */
    accent: string;
    
    /** Shadow effects for the theme */
    shadow: string;
  };
  
  /** Specific color values from the theme */
  colors: {
    primary400: string;
    primary600: string;
  };
}

// Create the theme context with undefined as initial value
export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  /** Initial theme to use (default: 'dark') */
  initialTheme?: ThemeMode;
  
  /** Whether to automatically detect user's preferred color scheme (default: true) */
  detectUserPreference?: boolean;
  
  /** Storage key for persisting theme preference */
  storageKey?: string;
  
  /** Enable debug logging (default: false) */
  debug?: boolean;
  
  /** Children components that will have access to the theme context */
  children: ReactNode;
}

/**
 * ThemeProvider component
 *
 * @param props - Component props
 * @returns ThemeProvider component
 */
const ThemeProvider: React.FC<ThemeProviderProps> = ({
  initialTheme = 'dark',
  detectUserPreference = true,
  storageKey = 'nova-theme',
  debug = false,
  children
}) => {
  // Removed Tailwind config resolution
  // const fullConfig = resolveConfig(tailwindConfig);

  // Initialize theme state with stored preference, user preference, or initialTheme prop
  const [currentTheme, setCurrentTheme] = useState<ThemeMode>(() => {
    // Check for stored preference first
    const storedTheme = localStorage.getItem(storageKey);
    if (storedTheme === 'light' || storedTheme === 'dark') {
      return storedTheme as ThemeMode;
    }
    
    // If no stored preference and detection is enabled, check system preference
    if (detectUserPreference) {
      const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDarkMode ? 'dark' : 'light';
    }
    
    // Fall back to initialTheme
    return initialTheme;
  });
  
  // Apply the theme to the HTML element and set body background
  useEffect(() => {
    const isDark = currentTheme === 'dark';

    // Define theme-specific color palettes - Increased opacity for light theme
    const lightColors = [
      'rgba(191, 219, 254, 0.8)', // blue-200 at 80%
      'rgba(221, 214, 254, 0.8)', // violet-200 at 80%
      'rgba(167, 243, 208, 0.65)', // emerald-200 at 65%
      'rgba(147, 197, 253, 0.6)',  // blue-300 at 60%
      'rgba(216, 180, 254, 0.5)'   // purple-200 at 50%
    ];
    const darkColors = [
      'rgba(7, 11, 26, 0.85)',     // Base #070B1A almost opaque
      'rgba(30, 27, 75, 0.6)',      // Deep Indigo at 60%
      'rgba(76, 29, 149, 0.5)',      // Deep Purple at 50%
      'rgba(42, 152, 251, 0.25)',    // Primary blue glow at 25%
      'rgba(21, 128, 61, 0.2)'       // Deep Green glow at 20%
    ];
    const currentColors = isDark ? darkColors : lightColors;
    const animationDuration = '10s';

    // Adjust gradient stops for more prominent shapes
    const backgroundStyle = `
      radial-gradient(ellipse at 15% 85%, ${currentColors[0]} 0%, transparent 50%),
      radial-gradient(ellipse at 80% 90%, ${currentColors[1]} 0%, transparent 45%),
      radial-gradient(ellipse at 50% 10%, ${currentColors[2]} 0%, transparent 40%),
      radial-gradient(ellipse at 90% 25%, ${currentColors[3]} 0%, transparent 55%),
      linear-gradient(135deg, transparent 20%, ${currentColors[4]} 50%, transparent 80%)
    `;
    // Base solid color remains important
    const baseBackgroundColor = isDark ? '#040510' : '#F3F4F6'; 

    // Keyframes with more varied position shifts
    const keyframesName = 'nebulaAnimation';
    const keyframes = `
      @keyframes ${keyframesName} {
        0% { background-position: 0% 50%, 100% 50%, 50% 0%, 50% 100%, 0% 0%; }
        25% { background-position: 20% 10%, 80% 30%, 10% 40%, 90% 80%, 30% 20%; } /* More drastic shifts */
        50% { background-position: 50% 0%, 50% 100%, 0% 50%, 100% 50%, 50% 50%; } /* Swapped some positions */
        75% { background-position: 80% 10%, 20% 30%, 90% 40%, 10% 80%, 70% 80%; } /* More drastic shifts */
        100% { background-position: 0% 50%, 100% 50%, 50% 0%, 50% 100%, 0% 0%; }
      }
    `;

    // Inject or update keyframes
    let keyframesStyleElement = document.getElementById('dynamic-background-keyframes');
    if (!keyframesStyleElement) {
      keyframesStyleElement = document.createElement('style');
      keyframesStyleElement.id = 'dynamic-background-keyframes';
      document.head.appendChild(keyframesStyleElement);
    } 
    keyframesStyleElement.textContent = keyframes;

    // Toggle the 'dark'/'light' class on the HTML element
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    }

    // Create or find the background element
    let themeBackgroundElement = document.getElementById('theme-background-element');
    
    if (!themeBackgroundElement) {
      themeBackgroundElement = document.createElement('div');
      themeBackgroundElement.id = 'theme-background-element';
      themeBackgroundElement.style.position = 'fixed';
      themeBackgroundElement.style.top = '0';
      themeBackgroundElement.style.left = '0';
      themeBackgroundElement.style.width = '100vw';
      themeBackgroundElement.style.height = '100vh';
      themeBackgroundElement.style.zIndex = '-20';
      themeBackgroundElement.style.pointerEvents = 'none';
      themeBackgroundElement.style.overflow = 'hidden';
      document.body.insertBefore(themeBackgroundElement, document.body.firstChild);
    }
    
    // Apply the enhanced animated background styles
    themeBackgroundElement.style.background = backgroundStyle;
    themeBackgroundElement.style.backgroundColor = baseBackgroundColor; 
    themeBackgroundElement.style.backgroundSize = '400% 400%'; 
    themeBackgroundElement.style.animation = `${keyframesName} ${animationDuration} ease-in-out infinite`;
    themeBackgroundElement.style.transition = 'background 1.5s ease-in-out';
    
    // Store the theme preference
    localStorage.setItem(storageKey, currentTheme);
    
    // Log theme change if debug is enabled
    if (debug) {
      themeLogger.debug(`Theme set to: ${currentTheme}`);
    }
    
    // Clean up on unmount
    return () => {
      // We don't remove the element on unmount as other theme providers might use it
    };
  }, [currentTheme, storageKey, debug]);
  
  // Detect user's preferred color scheme if enabled
  useEffect(() => {
    if (detectUserPreference) {
      // Only listen for changes in color scheme preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (event: MediaQueryListEvent) => {
        setCurrentTheme(event.matches ? 'dark' : 'light');
      };
      
      // Add event listener
      mediaQuery.addEventListener('change', handleChange);
      
      // Clean up
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
  }, [detectUserPreference]);
  
  // Toggle between light and dark themes
  const toggleTheme = useCallback(() => {
    setCurrentTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  }, []);
  
  // Explicitly set the theme
  const setTheme = useCallback((theme: ThemeMode) => {
    setCurrentTheme(theme);
  }, []);
  
  // Check if current theme is dark
  const isDarkTheme = currentTheme === 'dark';
  
  // Define theme-specific CSS classes
  const themeClasses = useMemo(() => {
    return {
      container: isDarkTheme 
        ? 'bg-gray-900' 
        : 'bg-white',
      text: isDarkTheme 
        ? 'text-white' 
        : 'text-gray-900',
      border: isDarkTheme 
        ? 'border-white/10' 
        : 'border-gray-200/10',
      card: isDarkTheme 
        ? 'bg-black/50 backdrop-blur-md' 
        : 'bg-white/70 backdrop-blur-md',
      accent: isDarkTheme 
        ? 'text-primary-400' 
        : 'text-primary-700',
      shadow: isDarkTheme 
        ? 'shadow-black/20' 
        : 'shadow-gray-200/50'
    };
  }, [isDarkTheme]);
  
  // Extract specific colors from Tailwind config for context
  const themeColors = useMemo(() => {
    // Directly use known hex values as importing tailwind.config.js outside src is problematic
    const p400 = '#39a5f7'; // Corresponds to primary-400
    const p600 = '#0e6fd3'; // Corresponds to primary-600
    
    return {
      primary400: p400,
      primary600: p600,
    };
  }, []); // Removed fullConfig from dependencies as it's no longer used

  // Create context value
  const contextValue = useMemo<ThemeContextType>(() => {
    return {
      theme: currentTheme,
      toggleTheme,
      setTheme,
      isDarkTheme,
      themeClasses,
      colors: themeColors, // Add resolved colors to context
    };
  }, [currentTheme, toggleTheme, setTheme, isDarkTheme, themeClasses, themeColors]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider; 
